import { HttpClient, HttpContext, HttpErrorResponse, httpResource } from '@angular/common/http';
import { Service, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable, map, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ResponseApi } from '../../../core/dtos/response-api';
import { HANDLE_HTTP_ERROR_LOCALLY, SKIP_SUCCESS_ALERT } from '../../../core/http/http-context-tokens';
import { AuthService } from '../../../core/services/auth/auth.service';
import {
  ErroCancelamentoSolicitacao,
  MinhaSolicitacao,
  MinhaSolicitacaoResponse,
} from '../models/minhas-solicitacoes.model';
import { statusParaAcompanhamento, textoPrazoSolicitacao } from '../models/solicitacao-servico.model';
import {
  paraEventoAcompanhamento,
  paraPendenciaAcompanhamento,
  paraRespostaAcompanhamento,
  tamanhoLegivel,
} from './solicitacao-servico-publica.service';
import { formatarData } from './solicitacoes.service';

const RECURSO = `${environment.apiUrl}/sai/cartaservico/MinhasSolicitacoes`;

/** O erro fica local: a aba e o modal mostram a própria falha, sem o modal do interceptor. */
const ERRO_LOCAL = () => new HttpContext().set(HANDLE_HTTP_ERROR_LOCALLY, true);

/** Erro local e sem o alerta automático de sucesso: o cancelamento parte de um `<dialog>`. */
const SILENCIOSO = () => ERRO_LOCAL().set(SKIP_SUCCESS_ALERT, true);

/**
 * As solicitações SEM agendamento de quem está logado, em `api/sai/cartaservico/MinhasSolicitacoes`
 * — a aba "Minhas solicitações": a lista (já com detalhe, respostas e histórico), o cancelamento
 * e o download dos anexos que o próprio cidadão enviou.
 *
 * A lista só é buscada com sessão **e** depois que a aba a pede (`carregar()`): o serviço é da
 * raiz, e sem o pedido ela sairia para qualquer tela que o injetasse. Ela depende do usuário —
 * cada login a refaz, e o logout a esquece.
 *
 * O complemento e o download dos anexos da RESPOSTA usam a superfície pública por protocolo
 * (`SolicitacaoServicoPublicaService`), que o dono também tem.
 */
@Service()
export class MinhasSolicitacoesService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);

  private readonly usuario = toSignal(this.auth.currentUser, {
    initialValue: this.auth.currentUserValue,
  });

  private readonly pedidas = signal(false);

  private readonly listaApi = httpResource<ResponseApi<MinhaSolicitacaoResponse[]>>(() => {
    // `undefined` é "nada a buscar": sem a guarda a chamada sairia sem token e voltaria 401.
    if (!this.pedidas() || !this.usuario()?.token) return undefined;

    return { url: RECURSO, context: ERRO_LOCAL() };
  });

  readonly falhou = computed(() => this.listaApi.error() !== undefined);

  /**
   * As solicitações, da abertura mais recente para a mais antiga, ou `null` sem sessão, antes do
   * pedido, na primeira carga ou em falha. O `error()` vem ANTES do `value()`: em erro o
   * `value()` lança. Durante o `reload()` o valor anterior fica — a lista não pisca.
   */
  readonly solicitacoes = computed<readonly MinhaSolicitacaoResponse[] | null>(() => {
    if (this.listaApi.error() !== undefined) return null;

    return this.listaApi.value()?.data ?? null;
  });

  /**
   * Pede a lista — a aba chama ao abrir. Chamada de novo (a aba foi reaberta), relê: o órgão
   * responde, encaminha e pede complemento sem ação do cidadão.
   */
  carregar(): void {
    if (this.pedidas()) {
      this.listaApi.reload();
    } else {
      this.pedidas.set(true);
    }
  }

  /** Refaz a chamada — o "Tentar novamente", e a releitura depois de um erro de estado. */
  recarregar(): void {
    this.listaApi.reload();
  }

  /** Cancela pelo protocolo, com o motivo, e relê a lista. */
  cancelar(protocolo: string, motivo: string): Observable<MinhaSolicitacaoResponse> {
    return this.http
      .post<ResponseApi<MinhaSolicitacaoResponse>>(
        `${rotaDaSolicitacao(protocolo)}/cancelamento`,
        { desMotivo: motivo.trim() },
        { context: SILENCIOSO() },
      )
      .pipe(
        map((resposta) => resposta.data),
        tap(() => this.listaApi.reload()),
      );
  }

  /**
   * O arquivo de um anexo que o próprio cidadão enviou, em `blob` — fora do envelope. O token
   * vai pelo `JwtInterceptor`; por isso o download não é um `<a href>` direto.
   */
  baixarAnexo(protocolo: string, codAnexoSolicitacaoAns: number): Observable<Blob> {
    return this.http.get(`${rotaDaSolicitacao(protocolo)}/anexo/${codAnexoSolicitacaoAns}`, {
      responseType: 'blob',
      context: ERRO_LOCAL(),
    });
  }
}

/** A rota da solicitação pelo protocolo — aparado, em maiúsculas e codificado. */
function rotaDaSolicitacao(protocolo: string): string {
  return `${RECURSO}/${encodeURIComponent(protocolo.trim().toUpperCase())}`;
}

/**
 * Uma solicitação da API no modelo que a aba renderiza — o mesmo do acompanhamento por
 * protocolo, com as conversões dele. Função pura: é a ponte entre os dois vocabulários de status.
 */
export function paraMinhaSolicitacao(resposta: MinhaSolicitacaoResponse): MinhaSolicitacao {
  return {
    protocolo: resposta.desProtocoloSsv,
    servico: resposta.desNomeServicoSsv,
    descricao: resposta.desDescricaoSsv?.trim() ?? '',
    endereco: resposta.desEndereco?.trim() ?? '',
    abertaEm: resposta.datInicioSsv.slice(0, 10),
    abertaAs: resposta.datInicioSsv.slice(11, 16),
    status: statusParaAcompanhamento(resposta.desStatusSsv),
    categoria: 'servico',
    tipo: 'Solicitação de serviço',
    prazo: textoPrazoSolicitacao(resposta),
    segundaVia: resposta.bitSegundaViaSsv,
    pendencia: resposta.pendenciaAberta
      ? paraPendenciaAcompanhamento(resposta.pendenciaAberta, resposta.desTipoContagemSsv)
      : undefined,
    arquivos: resposta.anexos.map((anexo) => ({
      id: `anexo-${anexo.codAnexoSolicitacaoAns}`,
      codigo: anexo.codAnexoSolicitacaoAns,
      nome: anexo.desNomeOriginalAns,
      data: formatarData(anexo.datInicioAns.slice(0, 10)),
      tamanho: tamanhoLegivel(anexo.numTamanhoBytesAns),
      url: '',
    })),
    historico: resposta.historico.map(paraEventoAcompanhamento),
    respostas: resposta.respostas.map(paraRespostaAcompanhamento),
    motivoCancelamento: resposta.desMotivoCancelamentoSsv?.trim() || undefined,
    podeCancelar: resposta.bitPodeCancelar,
  };
}

const LIMITE = 'Muitas tentativas em pouco tempo. Aguarde alguns instantes e tente novamente.';
const SESSAO_EXPIRADA = 'Sua sessão expirou. Entre novamente.';

/**
 * Traduz o erro do cancelamento para o que o modal mostra.
 *
 * A `message` da API é usada onde ela é a regra recusada (`400`, `404`, `409`, `422`); em `5xx`
 * e sem resposta ela **nunca** é mostrada — num erro de banco traz o texto do SQL Server.
 * `404`, `409` e `422` dizem que a solicitação mudou: a lista é relida.
 */
export function mapearErroCancelamento(erro: unknown): ErroCancelamentoSolicitacao {
  const resposta = erro instanceof HttpErrorResponse ? erro : null;
  const corpo = (resposta?.error ?? null) as Partial<ResponseApi<unknown>> | null;

  const mensagemApi = typeof corpo?.message === 'string' ? corpo.message.trim() : '';

  // A mensagem costuma repetir a única violação; sem o filtro ela apareceria duas vezes.
  const detalhes = (Array.isArray(corpo?.errors) ? corpo.errors : [])
    .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    .map((item) => item.trim())
    .filter((item) => item !== mensagemApi);

  switch (resposta?.status) {
    case 400:
      return {
        mensagem: mensagemApi || 'Não foi possível cancelar a solicitação com estes dados.',
        detalhes,
        recarregar: false,
      };

    case 401:
      // O interceptor já encerrou a sessão; a aba passa a mostrar o aviso de sessão.
      return { mensagem: SESSAO_EXPIRADA, detalhes: [], recarregar: false };

    case 404:
      return { mensagem: mensagemApi || 'Não encontramos esta solicitação.', detalhes: [], recarregar: true };

    case 409:
      return {
        mensagem:
          mensagemApi ||
          'A solicitação foi alterada enquanto você a cancelava. Atualize a lista e tente de novo.',
        detalhes: [],
        recarregar: true,
      };

    case 422:
      return {
        mensagem: mensagemApi || 'Esta solicitação não pode mais ser cancelada.',
        detalhes: [],
        recarregar: true,
      };

    case 429:
      // O 429 vem em texto puro, fora do envelope: é do pipeline, antes do handler.
      return { mensagem: LIMITE, detalhes: [], recarregar: false };

    default:
      return {
        mensagem: 'Não foi possível cancelar a solicitação agora. Tente novamente em instantes.',
        detalhes: [],
        recarregar: false,
      };
  }
}
